<template>
  <div
    class="convocation-reg-report"
    :class="{ 'convocation-reg-report--individual': isIndividual }"
  >
    <div class="no-print control-bar">
      <div class="d-flex align-items-center flex-wrap">
        <h4 class="mb-0">
          Convocation Registration
          <span class="text-primary">| Report</span>
        </h4>
        <b-badge
          v-show="!loading && !isIndividual"
          variant="light-success"
          class="ml-2"
        >
          {{ reportItems.length }}
          {{ reportItems.length === 1 ? "Record" : "Records" }}
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print Report</span>
        </button>
        <button class="btn-modern btn-close" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3.5rem; height: 3.5rem" />
      <p class="mt-3 font-weight-bold">Loading convocation registrations...</p>
    </div>

    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h4 class="text-danger font-weight-bold">{{ error }}</h4>
      <b-button variant="primary" class="mt-3" @click="loadReportData">
        Retry
      </b-button>
    </div>

    <!-- Individual: full-page certificate / profile sheet -->
    <div
      v-else-if="isIndividual && individualRecord"
      class="individual-sheet"
    >
      <header class="individual-sheet__header">
        <img
          v-if="schoolLogo"
          :src="schoolLogo"
          alt="Logo"
          class="individual-sheet__logo"
        />
        <div class="individual-sheet__header-text">
          <h1 class="individual-sheet__campus">
            {{ campusName
            }}<span v-if="campusBranch"> — {{ campusBranch }}</span>
          </h1>
          <p class="individual-sheet__subtitle">Convocation Registration</p>
        </div>
      </header>

      <section class="individual-sheet__hero">
        <img
          v-if="individualRecord.photoUrl"
          :src="individualRecord.photoUrl"
          alt=""
          class="individual-sheet__photo"
          referrerpolicy="no-referrer"
        />
        <div
          v-else
          class="individual-sheet__photo individual-sheet__photo--placeholder"
        >
          <feather-icon icon="UserIcon" size="48" />
        </div>
        <div class="individual-sheet__identity">
          <h2 class="individual-sheet__name">
            {{ fullName(individualRecord) }}
          </h2>
          <div class="individual-sheet__chips">
            <span class="chip">
              Class of {{ individualRecord.convocationYear || "—" }}
            </span>
            <span class="chip chip--muted">
              {{ individualRecord.departmentName || "—" }}
            </span>
          </div>
        </div>
      </section>

      <section class="individual-sheet__section">
        <h3 class="individual-sheet__section-title">Personal Information</h3>
        <div class="individual-sheet__grid">
          <div class="field">
            <span class="field__label">CNIC</span>
            <span class="field__value">{{ individualRecord.cnic || "—" }}</span>
          </div>
          <div class="field">
            <span class="field__label">Reg #</span>
            <span class="field__value">{{ individualRecord.regNo || "—" }}</span>
          </div>
          <div class="field">
            <span class="field__label">Date of birth</span>
            <span class="field__value">{{
              formatDate(individualRecord.dob)
            }}</span>
          </div>
          <div class="field">
            <span class="field__label">Gender</span>
            <span class="field__value">{{
              individualRecord.gender || "—"
            }}</span>
          </div>
          <div class="field">
            <span class="field__label">Cell / WhatsApp</span>
            <span class="field__value">{{
              individualRecord.cellPhone || "—"
            }}</span>
          </div>
          <div class="field">
            <span class="field__label">Tel</span>
            <span class="field__value">{{ individualRecord.tel || "—" }}</span>
          </div>
          <div class="field field--wide">
            <span class="field__label">Email</span>
            <span class="field__value">{{
              individualRecord.email || "—"
            }}</span>
          </div>
          <div class="field field--wide">
            <span class="field__label">Address</span>
            <span class="field__value">{{
              fullAddress(individualRecord)
            }}</span>
          </div>
        </div>
      </section>

      <section
        v-if="guestsOf(individualRecord).length"
        class="individual-sheet__section"
      >
        <h3 class="individual-sheet__section-title">Guests</h3>
        <table class="individual-guests-table">
          <thead>
            <tr>
              <th style="width: 10%">#</th>
              <th style="width: 45%">Name</th>
              <th style="width: 45%">Relationship</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(g, idx) in guestsOf(individualRecord)"
              :key="'guest-' + idx"
            >
              <td class="text-center">{{ idx + 1 }}</td>
              <td>{{ g.name || "—" }}</td>
              <td>{{ g.relationship || "—" }}</td>
            </tr>
          </tbody>
        </table>
      </section>

      <footer class="individual-sheet__footer">
        Generated on {{ currentDateTime }}
      </footer>
    </div>

    <!-- Overall directory -->
    <div v-else class="report-page">
      <div class="report-header-modern mb-4">
        <div class="d-flex align-items-center">
          <div v-if="schoolLogo" class="logo-wrapper mr-3">
            <img :src="schoolLogo" alt="Logo" class="school-logo-img" />
          </div>
          <div class="header-text-block">
            <h2 class="school-name-full mb-0">
              {{ campusName
              }}<span v-if="campusBranch" class="text-muted">
                - {{ campusBranch }}</span
              >
            </h2>
            <div class="report-info-line mt-1">
              <span class="report-name-text">Convocation Registration Report</span>
              <span class="date-range-text">
                <strong>Generated on {{ currentDateTime }}</strong>
              </span>
            </div>
          </div>
        </div>
      </div>

      <div class="table-container">
        <table class="ledger-style-table">
          <thead>
            <tr>
              <th class="text-center" style="width: 4%">#</th>
              <th class="text-left" style="width: 16%">Name</th>
              <th class="text-center" style="width: 8%">Year</th>
              <th class="text-left" style="width: 14%">Department</th>
              <th class="text-center" style="width: 10%">Reg #</th>
              <th class="text-center" style="width: 11%">CNIC</th>
              <th class="text-center" style="width: 11%">Cell</th>
              <th class="text-left" style="width: 14%">Email</th>
              <th class="text-left" style="width: 12%">Guests</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in reportItems" :key="item.id || index">
              <td class="text-center font-weight-bold">{{ index + 1 }}</td>
              <td class="text-left font-weight-bold">
                {{ fullName(item).toUpperCase() }}
              </td>
              <td class="text-center">{{ item.convocationYear || "—" }}</td>
              <td class="text-left">{{ item.departmentName || "—" }}</td>
              <td class="text-center gr-cell">{{ item.regNo || "—" }}</td>
              <td class="text-center">{{ item.cnic || "—" }}</td>
              <td class="text-center">{{ item.cellPhone || "—" }}</td>
              <td class="text-left email-cell">
                {{ (item.email || "n/a").toLowerCase() }}
              </td>
              <td class="text-left guests-cell">
                {{ guestsSummary(item) }}
              </td>
            </tr>
            <tr v-if="reportItems.length === 0">
              <td colspan="9" class="text-center py-5 no-data">
                <feather-icon
                  icon="InboxIcon"
                  size="32"
                  class="text-muted mb-2"
                />
                <p>
                  No convocation registrations found for the selected criteria
                </p>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from "bootstrap-vue";
import axios from "axios";

export default {
  name: "ConvocationRegistrationPrint",
  components: {
    BButton,
    BSpinner,
    BBadge,
  },
  data() {
    return {
      loading: false,
      error: null,
      reportItems: [],
      campusName: "MY SKOOL",
      campusBranch: "",
      schoolLogo: null,
      db: "",
      campusID: 0,
      years: "",
      departmentName: "",
      recordId: 0,
    };
  },
  computed: {
    isIndividual() {
      return !!this.recordId;
    },
    individualRecord() {
      return this.reportItems[0] || null;
    },
    currentDateTime() {
      const now = new Date();
      return now.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    },
  },
  created() {
    this.initReport();
  },
  methods: {
    initReport() {
      const query = this.$route.query;
      this.db = query.db || this.$store.state.userData?.db || "";
      this.campusID =
        Number(query.campusID ?? query.cID ?? this.$store.state.userData?.cId) ||
        0;
      this.years = query.years != null ? String(query.years) : "";
      this.departmentName =
        query.departmentName != null ? String(query.departmentName) : "";
      this.recordId = Number(query.id || query.recordId || 0) || 0;

      if (!this.db) {
        this.error =
          "Database identifier is missing. Please close and try again.";
        return;
      }

      this.loadCampusInfo();
      this.loadReportData();
    },

    apiBase() {
      const domain = (this.$store.state.domain || "").replace(/\/?$/, "/");
      if (domain) return domain;
      return "https://apiv26.myskool.app/api/";
    },

    async loadCampusInfo() {
      try {
        const url = `https://apiv26.myskool.app/api/campuses`;
        const response = await axios.get(url, {
          params: { db: this.db, cId: this.campusID },
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: "application/json",
          },
        });

        if (
          response.data &&
          response.data.status === "success" &&
          Array.isArray(response.data.data)
        ) {
          const match = response.data.data.find((c) => c.id == this.campusID);
          if (match) {
            this.campusName = match.name;
            this.campusBranch =
              this.campusID == 0 ? "All Campuses" : match.branch;
            this.schoolLogo = match.logo;
          }
        }
      } catch (e) {
        console.warn("Campus info fetch failed, using fallback from store");
        const branches = this.$store.state.branches || [];
        const currentBranch =
          branches.find((b) => b.id == this.campusID) ||
          this.$store.state.currentBranch;
        if (currentBranch) {
          this.campusName =
            currentBranch.organizationName ||
            currentBranch.branch ||
            "MY SKOOL";
          this.campusBranch = currentBranch.branch || "";
          this.schoolLogo = currentBranch.logo || null;
        }
      }
    },

    normalizeList(payload) {
      if (!payload) return [];
      if (Array.isArray(payload)) return payload;
      if (Array.isArray(payload.data)) return payload.data;
      if (Array.isArray(payload.items)) return payload.items;
      if (Array.isArray(payload.rows)) return payload.rows;
      if (payload.data && Array.isArray(payload.data.data)) {
        return payload.data.data;
      }
      return [];
    },

    async loadReportData() {
      this.loading = true;
      this.error = null;

      try {
        const params = {
          db: this.db,
          campusID: this.campusID,
        };
        if (this.years) params.years = this.years;
        if (this.departmentName) params.departmentName = this.departmentName;
        if (this.recordId) params.id = this.recordId;

        const response = await axios.get(
          `${this.apiBase()}ConvocationRegistration/LoadReport`,
          {
            params,
            headers: {
              Authorization: `Bearer ${this.$store.state.userData.token}`,
              accept: "application/json",
            },
          }
        );

        let rows = [];
        if (response.data && response.data.status === "success") {
          rows = this.normalizeList(response.data.data ?? response.data);
        } else if (Array.isArray(response.data)) {
          rows = response.data;
        } else if (response.data && Array.isArray(response.data.data)) {
          rows = response.data.data;
        } else {
          this.error =
            response.data?.message ||
            "The server returned an unexpected response format.";
          this.reportItems = [];
          return;
        }

        if (this.recordId) {
          rows = rows.filter((r) => Number(r.id) === Number(this.recordId));
          if (!rows.length) {
            this.error = "Registration record not found for printing.";
            this.reportItems = [];
            return;
          }
        }

        this.reportItems = rows;
      } catch (err) {
        console.error("Error fetching convocation report:", err);
        this.error =
          "Failed to retrieve report data. Please ensure the API endpoint is accessible.";
        this.reportItems = [];
      } finally {
        this.loading = false;
      }
    },

    fullName(item) {
      if (!item) return "—";
      const name = `${item.firstName || ""} ${item.lastName || ""}`.trim();
      return name || item.fullName || "—";
    },

    fullAddress(item) {
      if (!item) return "—";
      const parts = [item.addressLine1, item.addressLine2]
        .map((s) => String(s || "").trim())
        .filter(Boolean);
      return parts.length ? parts.join(", ") : "—";
    },

    guestsOf(item) {
      if (!item) return [];
      let list = item.guests;
      if (!Array.isArray(list) || !list.length) {
        const raw = item.guestsJson ?? item.GuestsJson ?? item.guests_json;
        if (Array.isArray(raw)) list = raw;
        else if (typeof raw === "string" && raw.trim()) {
          try {
            const parsed = JSON.parse(raw);
            list = Array.isArray(parsed) ? parsed : [];
          } catch {
            list = [];
          }
        } else {
          list = [];
        }
      }
      return (list || []).filter(
        (g) =>
          g &&
          (String(g.name || "").trim() || String(g.relationship || "").trim())
      );
    },

    guestsSummary(item) {
      const guests = this.guestsOf(item);
      if (!guests.length) return "—";
      return guests
        .map((g) => {
          const name = String(g.name || "").trim();
          const rel = String(g.relationship || "").trim();
          if (name && rel) return `${name} (${rel})`;
          return name || rel;
        })
        .join(", ");
    },

    formatDate(value) {
      if (!value) return "—";
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return String(value);
      return d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },

    print() {
      window.print();
    },

    close() {
      window.close();
    },
  },
};
</script>

<style lang="scss" scoped>
.convocation-reg-report {
  background: #f0f2f5;
  min-height: 100vh;
  padding: 2.5rem;
  font-family: "Outfit", "Inter", sans-serif;
  color: #2d3436;

  &--individual {
    padding: 1.5rem;
  }
}

.control-bar {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 1.25rem 2.5rem;
  margin-bottom: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 1rem;
  z-index: 1000;
  border: 1px solid rgba(255, 255, 255, 0.3);
  gap: 1rem;
  flex-wrap: wrap;

  h4 {
    color: #444;
    font-weight: 700;
    font-size: 1.25rem;
    letter-spacing: -0.5px;
  }
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.btn-modern {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 24px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  outline: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  span {
    margin-left: 4px;
  }

  &:active {
    transform: scale(0.96);
  }
}

.btn-print {
  background: linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%);
  color: white;

  &:hover {
    background: linear-gradient(135deg, #5b4cc4 0%, #8c82eb 100%);
    box-shadow: 0 6px 20px rgba(108, 92, 231, 0.35);
    transform: translateY(-1px);
  }
}

.btn-close {
  background: white;
  color: #636e72;
  border: 1px solid #dfe6e9;

  &:hover {
    background: #fdfdfd;
    color: #e74c3c;
    border-color: #fab1a0;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.05);
    transform: translateY(-1px);
  }
}

/* ---- Overall list page ---- */
.report-page {
  background: #ffffff;
  padding: 3rem 2.5rem;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
  width: 98%;
  max-width: 1200px;
  margin: 30px auto;
  box-sizing: border-box;
}

.report-header-modern {
  border-bottom: 2px solid #333;
  padding-bottom: 20px;

  .school-logo-img {
    height: 130px;
    width: auto;
    object-fit: contain;
  }

  .school-name-full {
    font-size: 2rem;
    font-weight: 800;
    color: #000;
    text-transform: uppercase;
    letter-spacing: -0.5px;
  }

  .report-info-line {
    font-size: 1.1rem;
    color: #444;
    display: flex;
    align-items: center;
    flex-wrap: wrap;

    .report-name-text {
      font-weight: 700;
      border-right: 2px solid #ddd;
      padding-right: 15px;
      margin-right: 15px;
    }

    .date-range-text {
      color: #555;
    }
  }
}

.ledger-style-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
  border: 1px solid #dee2e6;
  background: white;

  th,
  td {
    border: 1px solid #dee2e6;
    padding: 10px 12px;
    box-sizing: border-box;
    font-size: 0.9rem;
    vertical-align: top;
  }

  thead {
    background: #2c3e50 !important;

    th {
      color: white !important;
      font-weight: 800;
      text-transform: uppercase;
      font-size: 0.78rem;
      letter-spacing: 0.4px;
    }
  }

  tbody tr:nth-child(even) {
    background-color: #f8f9fa;
  }

  .gr-cell {
    font-weight: 700;
    color: #6c5ce7;
  }

  .email-cell,
  .guests-cell {
    word-break: break-word;
  }
}

/* ---- Individual full-page sheet ---- */
.individual-sheet {
  background: #fff;
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  min-height: calc(100vh - 7rem);
  padding: 2.75rem 2.5rem 2rem;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

.individual-sheet__header {
  display: flex;
  align-items: center;
  gap: 1.25rem;
  padding-bottom: 1.5rem;
  border-bottom: 3px solid #1e293b;
  margin-bottom: 2rem;
}

.individual-sheet__logo {
  height: 110px;
  width: auto;
  max-width: 220px;
  object-fit: contain;
  flex-shrink: 0;
}

.individual-sheet__campus {
  margin: 0;
  font-size: 1.85rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: -0.4px;
  color: #0f172a;
  line-height: 1.2;
}

.individual-sheet__subtitle {
  margin: 0.4rem 0 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #475569;
}

.individual-sheet__hero {
  display: flex;
  align-items: center;
  gap: 1.75rem;
  padding: 1.75rem 1.5rem;
  margin-bottom: 2rem;
  background: linear-gradient(180deg, #f8f7ff 0%, #ffffff 100%);
  border: 1px solid #e0d8ff;
  border-radius: 16px;
}

.individual-sheet__photo {
  width: 140px;
  height: 140px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  border: 4px solid #fff;
  box-shadow: 0 8px 24px rgba(115, 103, 240, 0.2);

  &--placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f3f0ff;
    color: #7367f0;
  }
}

.individual-sheet__name {
  margin: 0 0 0.75rem;
  font-size: 2.15rem;
  font-weight: 800;
  color: #0f172a;
  line-height: 1.15;
  word-break: break-word;
}

.individual-sheet__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.chip {
  display: inline-flex;
  align-items: center;
  padding: 0.4rem 0.9rem;
  border-radius: 999px;
  background: #7367f0;
  color: #fff;
  font-weight: 700;
  font-size: 0.95rem;

  &--muted {
    background: #eef2ff;
    color: #4338ca;
  }
}

.individual-sheet__section {
  margin-bottom: 1.75rem;
  flex: 1 1 auto;
}

.individual-sheet__section-title {
  margin: 0 0 1rem;
  font-size: 0.95rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #64748b;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #e2e8f0;
}

.individual-sheet__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.15rem 1.75rem;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  padding: 0.85rem 1rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  min-height: 72px;

  &--wide {
    grid-column: 1 / -1;
  }

  &__label {
    font-size: 0.72rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #94a3b8;
  }

  &__value {
    font-size: 1.15rem;
    font-weight: 700;
    color: #1e293b;
    word-break: break-word;
    line-height: 1.35;
  }
}

.individual-guests-table {
  width: 100%;
  border-collapse: collapse;
  border: 1px solid #cbd5e1;

  th,
  td {
    border: 1px solid #cbd5e1;
    padding: 0.85rem 1rem;
    font-size: 1.05rem;
  }

  thead th {
    background: #1e293b;
    color: #fff;
    text-transform: uppercase;
    font-size: 0.8rem;
    letter-spacing: 0.05em;
    font-weight: 800;
  }

  tbody tr:nth-child(even) {
    background: #f8fafc;
  }
}

.individual-sheet__footer {
  margin-top: auto;
  padding-top: 1.25rem;
  border-top: 1px solid #e2e8f0;
  text-align: center;
  color: #94a3b8;
  font-size: 0.9rem;
  font-weight: 600;
}

@media print {
  .no-print {
    display: none !important;
  }

  html,
  body {
    background: white !important;
    margin: 0 !important;
    padding: 0 !important;
    height: auto !important;
    overflow: visible !important;
  }

  .convocation-reg-report {
    background: white !important;
    padding: 0 !important;
    margin: 0 !important;
    min-height: 0 !important;
    height: auto !important;
  }

  .convocation-reg-report--individual {
    padding: 0 !important;
    min-height: 0 !important;
  }

  .report-page {
    box-shadow: none !important;
    border: none !important;
    padding: 0.8cm 0.6cm !important;
    max-width: 100% !important;
    width: 100% !important;
    margin: 0 !important;
  }

  .ledger-style-table {
    font-size: 8.5pt !important;
    width: 100% !important;

    th,
    td {
      padding: 5px 7px !important;
      border: 1px solid #000 !important;
      color: #000 !important;
    }

    thead th {
      background: #f1f2f6 !important;
      color: #000 !important;
      font-weight: 800 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }

  .report-header-modern {
    margin-bottom: 14px !important;
    padding-bottom: 10px !important;
    border-bottom: 3px solid #000 !important;

    .school-logo-img {
      height: 90px !important;
      max-width: 240px !important;
    }

    .school-name-full {
      font-size: 1.45rem !important;
    }
  }

  .individual-sheet {
    box-shadow: none !important;
    border: none !important;
    border-radius: 0 !important;
    max-width: 100% !important;
    width: 100% !important;
    margin: 0 !important;
    min-height: 0 !important;
    height: auto !important;
    padding: 1.1cm 1cm 0.8cm !important;
    page-break-after: avoid !important;
    page-break-inside: avoid !important;
    break-after: avoid-page !important;
    break-inside: avoid !important;
  }

  .individual-sheet__header {
    margin-bottom: 1.25rem !important;
    padding-bottom: 0.85rem !important;
  }

  .individual-sheet__logo {
    height: 95px !important;
  }

  .individual-sheet__campus {
    font-size: 1.55rem !important;
  }

  .individual-sheet__hero {
    margin-bottom: 1.25rem !important;
    padding: 1.1rem !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .individual-sheet__photo {
    width: 120px !important;
    height: 120px !important;
  }

  .individual-sheet__name {
    font-size: 1.85rem !important;
  }

  .field {
    background: #f8fafc !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
    min-height: 64px !important;

    &__value {
      font-size: 12pt !important;
    }
  }

  .individual-guests-table {
    thead th {
      background: #f1f2f6 !important;
      color: #000 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    th,
    td {
      border-color: #000 !important;
      padding: 8px 10px !important;
    }
  }

  .individual-sheet__footer {
    margin-top: 1rem !important;
  }

  .chip {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
}
</style>

<style lang="scss">
/* Global print page rules (not scoped) — prevents blank 2nd page */
@media print {
  @page {
    size: A4 portrait;
    margin: 8mm;
  }

  html,
  body,
  #app {
    height: auto !important;
    min-height: 0 !important;
    overflow: visible !important;
    background: #fff !important;
  }
}
</style>
